// S-L10: LOC Terms Acceptance Drawer
import { useState } from 'react';
import { Drawer, Button, Typography, Space, Checkbox, message, Divider } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { creditLineEndpoints } from '../api/creditLineEndpoints';

const { Title, Paragraph, Text } = Typography;

interface LocTermsDrawerProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
}

export function LocTermsDrawer({ open, onClose, onAccept }: LocTermsDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleAccept = async () => {
    if (!confirmed) {
      message.warning('Please confirm that you have read and agree to the terms.');
      return;
    }

    try {
      setLoading(true);
      await creditLineEndpoints.acceptTerms();
      message.success('LOC terms accepted successfully! Your credit line is now active.');
      setConfirmed(false);
      onAccept();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to accept terms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmed(false);
    onClose();
  };

  return (
    <Drawer
      title={
        <Space>
          <FileTextOutlined />
          <span>Line of Credit Agreement - Terms & Conditions</span>
        </Space>
      }
      placement="right"
      width={720}
      open={open}
      onClose={handleClose}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleAccept}
            loading={loading}
            disabled={!confirmed}
          >
            Accept & Activate LOC
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Line of Credit Terms (Version 1.0)</Title>
          <Paragraph>
            By accepting these terms, you agree to activate your Line of Credit and acknowledge the following conditions:
          </Paragraph>
        </div>

        <Divider />

        <div>
          <Title level={5}>1. Credit Limit & Availability</Title>
          <Paragraph>
            Your Credit Limit is determined by your account's Base Principal (initial investment) multiplied by 3×.
            The Available Credit is calculated as: <Text code>Credit Limit - Outstanding Balance - Accrued Interest</Text>.
          </Paragraph>
        </div>

        <div>
          <Title level={5}>2. Credit Requests</Title>
          <Paragraph>
            You may request credit against your Available Credit at any time. Credit requests require administrative approval
            before funds are disbursed. Only one pending credit request is allowed at a time.
          </Paragraph>
        </div>

        <div>
          <Title level={5}>3. Interest Accrual</Title>
          <Paragraph>
            Interest accrues daily on your Outstanding Balance at the current Annual Percentage Rate (APR).
            The APR is determined monthly and may vary based on market conditions. Interest compounds daily and is
            added to your Outstanding Balance at the end of each month.
          </Paragraph>
        </div>

        <div>
          <Title level={5}>4. Automatic Repayment (Paydown)</Title>
          <Paragraph>
            Your Outstanding Balance is automatically reduced through daily profit allocation. Each day, 100% of your
            allocated profit is applied as a payment toward your balance until fully repaid. This ensures continuous
            debt reduction without manual intervention.
          </Paragraph>
        </div>

        <div>
          <Title level={5}>5. Account Closure</Title>
          <Paragraph>
            If you request account closure while an Outstanding Balance exists, your LOC will be frozen (preventing new draws)
            and you must submit a final payoff payment to clear your balance before closure is finalized.
          </Paragraph>
        </div>

        <div>
          <Title level={5}>6. Modification & Termination</Title>
          <Paragraph>
            The company reserves the right to modify these terms, adjust your Credit Limit, or suspend/terminate your
            Line of Credit at any time with reasonable notice. You may request to close your LOC at any time, subject
            to repayment of all outstanding amounts.
          </Paragraph>
        </div>

        <Divider />

        <Checkbox
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        >
          <Text strong>
            I have read, understood, and agree to the Line of Credit Terms & Conditions (Version 1.0).
          </Text>
        </Checkbox>
      </Space>
    </Drawer>
  );
}
